import React, { useEffect, useState } from 'react'
import {Sparkles, ArrowRight} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { sanitizeAgentList } from '@/utils/agentSecurity'

interface RecommendationEngineProps {
  userId?: string
  onAgentClick: (agent: any) => void
}

export function RecommendationEngine({ userId, onAgentClick }: RecommendationEngineProps) {
  const [recommendations, setRecommendations] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (userId) {
      fetchRecommendations()
    }
  }, [userId])

  const fetchRecommendations = async () => {
    setLoading(true)
    try {
      // Get user's task history to understand preferences
      const { list: userTasks } = await lumi.entities.tasks.list({
        filter: { requesterId: userId },
        sort: { createdAt: -1 },
        limit: 5
      })

      if (userTasks.length === 0) {
        // New user - recommend top rated agents
        const { list: topAgents } = await lumi.entities.agents.list({
          filter: { availability: 'online' },
          sort: { rating: -1 },
          limit: 3
        })
        // SECURITY: Sanitize agent data to remove sensitive fields
        const sanitizedAgents = sanitizeAgentList(topAgents)
        setRecommendations(sanitizedAgents)
        setLoading(false)
        return
      }

      // Extract preferred categories
      const categoryCount: Record<string, number> = {}
      userTasks.forEach(task => {
        categoryCount[task.category] = (categoryCount[task.category] || 0) + 1
      })

      const topCategory = Object.entries(categoryCount).sort((a, b) => b[1] - a[1])[0]?.[0]

      // Find agents in preferred category
      const { list: matchedAgents } = await lumi.entities.agents.list({
        filter: {
          category: topCategory,
          availability: 'online'
        },
        sort: { rating: -1 },
        limit: 3
      })

      // SECURITY: Sanitize agent data to remove sensitive fields
      const sanitizedAgents = sanitizeAgentList(matchedAgents)
      setRecommendations(sanitizedAgents)
    } catch (error) {
      console.error('Failed to fetch recommendations:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!userId || loading || recommendations.length === 0) return null

  return (
    <div className="mb-12 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-2xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Recommended for You</h2>
          <p className="text-sm text-slate-400">Based on your recent activity</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {recommendations.map((agent) => (
          <div
            key={agent._id}
            onClick={() => onAgentClick(agent)}
            className="bg-slate-900/50 border border-purple-500/30 rounded-xl p-4 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all cursor-pointer group"
          >
            <div className="flex items-center gap-3 mb-3">
              {agent.profilePicture ? (
                <img
                  src={agent.profilePicture}
                  alt={agent.name}
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.classList.remove('hidden')
                  }}
                />
              ) : null}
              <div className={`w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold ${agent.profilePicture ? 'hidden' : ''}`}>
                {agent.name.charAt(0)}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white group-hover:text-purple-400 transition-colors">
                  {agent.name}
                </h3>
                <div className="text-xs text-slate-400">★ {agent.rating} • {agent.tasksCompleted} tasks</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">${agent.pricePerTask}/task</span>
              <ArrowRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
